import React, { useState, useEffect, useCallback } from 'react';
import HiraganaLoader from '../../components/HiraganaLoader';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../app/constants';

function SessionStats() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    // Analysis variables removed - now sent via email
    const navigate = useNavigate();
    const { session_id } = useParams();

    // Analysis generation removed - now handled in background and sent via email

    // Analysis fetching removed - now sent via email

    const fetchSessionData = useCallback(async () => {
        try {
            const jwt = localStorage.getItem('jwt');
            if (!jwt) {
                throw new Error("JWT token not found in localStorage");
            }
            const response = await fetch(`${API_URL}/api/v1/panel/session/analytics/${session_id}`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            console.log("🔍 DEBUG: Session data received:", data);
            console.log("🔍 DEBUG: Analysis data:", data.analysis_data);
            setSessionData(data);
            // Analysis now sent via email instead of frontend display
        } catch (error) {
            console.error("Error fetching session data:", error);
            setError("Error fetching session data");
        } finally {
            setLoading(false);
        }
    }, [session_id]);

    useEffect(() => {
        // Add a 2-second delay to ensure backend has finished processing the session
        const delayTimer = setTimeout(() => {
            fetchSessionData();
        }, 2000);

        return () => clearTimeout(delayTimer);
    }, [fetchSessionData]);

    if (loading) {
        return <HiraganaLoader />;
    }
    
    if (error) {
        return (
            <div style={{
                padding: '20px',
                textAlign: 'center',
                fontFamily: 'Manrope, sans-serif',
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#FFFFFF',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                <h1 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '12px'
                }}>
                    Unable to load session data
                </h1>
                <p style={{
                    color: '#6B7280',
                    marginBottom: '24px',
                    fontSize: '14px'
                }}>
                    {error}
                </p>
                <button 
                    onClick={() => navigate('/study_plan')} 
                    style={{
                        padding: '8px 16px',
                        background: '#111827',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    Back to Training
                </button>
            </div>
        );
    }

    if (!sessionData) {
        return <HiraganaLoader />;
    }

    const sectionsPassed = sessionData.section_scores ? 
        Object.values(sessionData.section_scores).every(section => section.score_percentage >= 50) : 
        sessionData.overall_score_percentage >= 50;

    return (
        <div style={{
            fontFamily: 'Manrope, sans-serif',
            width: '100%',
            minHeight: '100vh',
            padding: '20px',
            backgroundColor: '#FFFFFF',
            maxWidth: '1200px',
            margin: '0 auto'
        }}>
            {/* Header */}
            <div style={{ 
                marginBottom: '48px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h1 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0'
                }}>
                    Session Complete
                </h1>
                <button
                    onClick={() => {
                        navigate('/study_plan');
                    }}
                    style={{
                        backgroundColor: '#9334E9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Manrope, sans-serif'
                    }}
                >
                    Continue Training
                </button>
            </div>

            {/* Performance Summary */}
            <div style={{ marginBottom: '48px' }}>
                <h2 style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '20px'
                }}>
                    Performance Summary
                </h2>
                
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '24px'
                }}>
                    <div>
                        <div style={{
                            fontSize: '13px',
                            color: '#6B7280',
                            marginBottom: '4px'
                        }}>
                            Overall Score
                        </div>
                        <div style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            color: '#111827'
                        }}>
                            {sessionData.overall_score_percentage || 0}%
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: '#6B7280'
                        }}>
                            {sessionData.total_correct || 0} of {sessionData.total_questions || 0} questions
                        </div>
                    </div>
                    
                    {sessionData.section_scores && Object.entries(sessionData.section_scores).map(([section, { score_percentage }]) => (
                        <div key={section}>
                            <div style={{
                                fontSize: '13px',
                                color: '#6B7280',
                                marginBottom: '4px'
                            }}>
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </div>
                            <div style={{
                                fontSize: '24px',
                                fontWeight: '600',
                                color: '#111827'
                            }}>
                                {score_percentage}%
                            </div>
                            <div style={{
                                fontSize: '13px',
                                color: '#6B7280'
                            }}>
                                Section performance
                            </div>
                        </div>
                    ))}
                    
                    <div>
                        <div style={{
                            fontSize: '13px',
                            color: '#6B7280',
                            marginBottom: '4px'
                        }}>
                            Status
                        </div>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: sectionsPassed ? '#059669' : '#DC2626'
                        }}>
                            {sectionsPassed ? 'Great work!' : 'Keep practicing!'}
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: '#6B7280'
                        }}>
                            {sectionsPassed ? 'You\'re improving' : 'Focus on weak areas'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Analysis Email Notice */}
            <div style={{ marginBottom: '48px' }}>
                <h2 style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '20px'
                }}>
                    Session Analysis
                </h2>
                
                <div style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    lineHeight: '1.6',
                    backgroundColor: '#F9FAFB',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                }}>
                    📧 Your personalized session analysis will be sent to your email shortly. 
                    Check your inbox for detailed performance insights and recommendations!
                </div>
            </div>

            {/* Encouragement */}
            {!sectionsPassed && (
                <div style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    fontStyle: 'italic',
                    marginTop: '32px',
                    textAlign: 'center',
                    lineHeight: '1.5'
                }}>
                    Keep going! Every session brings you closer to your JLPT goal.
                </div>
            )}
        </div>
    );
}

export default SessionStats;